import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom-original'

import { ScreenType, useScreenType } from '@/modules/shared/hooks/useScreenType'
import { isDocumentUnread, patchDocumentAsRead } from '../../helpers/document-read-status.helpers'
import { usePatientDocumentMarkAsRead } from '../../hooks/usePatientDocumentMarkAsRead'
import { usePatientDocuments } from '../../hooks/usePatientDocuments'
import { DocumentsDesktopOverview } from './DocumentsDesktopOverview'
import { DocumentsMobileOverview } from './DocumentsMobileOverview'

import type {
  PatientDocumentsFilterOption,
  PatientViewDocument,
  PatientViewDocumentsByYear,
} from '../../types/patient-documents.types'

export const DocumentOverview = () => {
  const screenType = useScreenType()
  const routeParameters = useParams()
  const navigate = useNavigate()
  const { markAsRead } = usePatientDocumentMarkAsRead()

  const [selectedFilter, setSelectedFilter] = useState<PatientDocumentsFilterOption>('all')

  const { data: documentsResponse, isLoading } = usePatientDocuments({
    filter: selectedFilter,
    groupBy: 'year',
  })

  const selectedDocumentId = routeParameters?.id

  const [localDocumentsByYear, setLocalDocumentsByYear] = useState<PatientViewDocumentsByYear>({})

  const lastFilterRef = useRef<PatientDocumentsFilterOption>(selectedFilter)

  const sortDocuments = (documents: PatientViewDocument[]) => {
    return [...documents].sort((a, b) => {
      // Primary: unread before read
      const aUnread = isDocumentUnread(a)
      const bUnread = isDocumentUnread(b)
      if (aUnread && !bUnread) return -1
      if (!aUnread && bUnread) return 1

      // Secondary: newest first
      const dateA = new Date(a.date).getTime()
      const dateB = new Date(b.date).getTime()
      if (dateB !== dateA) return dateB - dateA

      // Tertiary: stable by id
      return a.id.localeCompare(b.id)
    })
  }

  const normalizeByYear = (data: unknown): PatientViewDocumentsByYear => {
    return (data as PatientViewDocumentsByYear) ?? {}
  }

  const mergePreservingOrder = (
    previous: PatientViewDocumentsByYear,
    incoming: PatientViewDocumentsByYear,
  ): PatientViewDocumentsByYear => {
    const previousIsEmpty = Object.keys(previous).length === 0
    if (previousIsEmpty) {
      const sorted: PatientViewDocumentsByYear = {}
      Object.entries(incoming).forEach(([year, documentation]) => {
        sorted[Number(year)] = sortDocuments(documentation)
      })
      return sorted
    }

    const merged: PatientViewDocumentsByYear = {}
    const years = new Set([...Object.keys(previous).map(Number), ...Object.keys(incoming).map(Number)])

    years.forEach((year) => {
      const previousDocumentation = previous[year] ?? []
      const nextDocumentation = incoming[year] ?? []
      const nextById = new Map(nextDocumentation.map((d) => [d.id, d]))
      const previousIds = new Set(previousDocumentation.map((d) => d.id))

      // Keep the existing order, just refresh the document objects from incoming.
      const kept = previousDocumentation.map((d) => nextById.get(d.id)).filter(Boolean) as PatientViewDocument[]

      // Add truly new documents (not seen before) sorted in the usual way.
      const added = sortDocuments(nextDocumentation.filter((d) => !previousIds.has(d.id)))

      merged[year] = [...added, ...kept]
    })

    return merged
  }

  useEffect(() => {
    if (documentsResponse?.data) {
      const incoming = normalizeByYear(documentsResponse.data)

      // When filter changes, rebuild order from scratch (unread first, newest first).
      if (lastFilterRef.current !== selectedFilter) {
        const sorted: PatientViewDocumentsByYear = {}
        Object.entries(incoming).forEach(([year, documentation]) => {
          sorted[Number(year)] = sortDocuments(documentation)
        })
        setLocalDocumentsByYear(sorted)
        lastFilterRef.current = selectedFilter
        return
      }

      // Otherwise, merge updates (status/count changes) without reordering.
      setLocalDocumentsByYear((previous) => mergePreservingOrder(previous, incoming))
    }
  }, [documentsResponse?.data, selectedFilter])

  const filterMetadataMap = useMemo(() => {
    const filterMetadata = documentsResponse?.meta?.filters ?? []
    return new Map(filterMetadata.map((meta) => [meta.filter, meta.openCount]))
  }, [documentsResponse?.meta?.filters])

  const handleDocumentClick = (documentId: string) => {
    const foundDocument = Object.values(localDocumentsByYear)
      .flat()
      .find((document) => document.id === documentId)

    if (!foundDocument) {
      return
    }

    // DocumentFlow: only mark as read when actually signed, not on click
    // PatientCommunication: mark message as read when opening (red dot in document list stays until attachment is opened)
    // Message: mark as read when opening
    if (foundDocument.type === 'PatientCommunication') {
      markAsRead(documentId, foundDocument, { scope: 'message' })
      setLocalDocumentsByYear((previous) =>
        Object.fromEntries(
          Object.entries(previous).map(([year, documents]) => [
            year,
            documents.map((d) => (d.id === documentId ? patchDocumentAsRead(d, 'message') : d)),
          ]),
        ),
      )
    } else if (foundDocument.type !== 'DocumentFlow' && isDocumentUnread(foundDocument, 'documents')) {
      markAsRead(documentId, foundDocument)
      setLocalDocumentsByYear((previous) =>
        Object.fromEntries(
          Object.entries(previous).map(([year, documents]) => [
            year,
            documents.map((d) => (d.id === documentId ? patchDocumentAsRead(d) : d)),
          ]),
        ),
      )
    }

    navigate(`${documentId}`)
  }

  if (screenType !== ScreenType.Mobile) {
    return (
      <DocumentsDesktopOverview
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        documentsByYear={localDocumentsByYear}
        isLoading={isLoading}
        unreadFiles={documentsResponse?.meta?.openCount ?? 0}
        selectedDocumentId={selectedDocumentId}
        filterMetadataMap={filterMetadataMap}
        handleDocumentClick={handleDocumentClick}
      />
    )
  }

  return (
    <DocumentsMobileOverview
      selectedFilter={selectedFilter}
      setSelectedFilter={setSelectedFilter}
      documentsByYear={localDocumentsByYear}
      isLoading={isLoading}
      selectedDocumentId={selectedDocumentId}
      filterMetadataMap={filterMetadataMap}
      handleDocumentClick={handleDocumentClick}
    />
  )
}
